import { useCallback, useEffect, useMemo, useState } from 'react'
import { adminStats } from '../../lib/admin'
import type { ActivityKind, AdminStats } from '../../lib/admin'
import Icon from '../../components/Icon'
import type { IconName } from '../../components/Icon'
import { AdminPage } from './Admin'
import { Meter, WeeklyColumns } from './charts'

/**
 * The numbers worth looking at.
 *
 * Deliberately no daily-actives line. A tour manager uses Encore hard for three
 * weeks then not at all for two months — that's the job, not churn. What tells
 * you the product works is whether they come back and plan a *second* tour, and
 * whether the crew they drag in convert into accounts.
 *
 * The three trend charts are small multiples on purpose. Signups and tours are
 * measures of wildly different scale, and putting them on one plot with two
 * y-axes would let you draw any story you liked out of the same data.
 */
export default function AdminOverview() {
  const [stats, setStats] = useState<AdminStats | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    return adminStats({})
      .then(setStats)
      .catch((caught: Error) => setError(caught.message))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  return (
    <AdminPage
      title="Overview"
      lede="Signups and tours are the top line. Activation and invite acceptance are the ones that actually predict whether this grows."
      actions={
        <div className="row" style={{ gap: 10, alignItems: 'center' }}>
          {stats && (
            <span className="t-spec-caption faint">
              As of {new Intl.DateTimeFormat('en-GB', {
                hour: '2-digit',
                minute: '2-digit',
                hourCycle: 'h23',
              }).format(new Date(stats.generatedAt))}
            </span>
          )}
          <button className="btn btn-secondary" disabled={loading} onClick={() => void load()}>
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      }
    >
      {loading && !stats && <p className="t-caption">Loading…</p>}
      {error && (
        <p className="panel panel-pad t-caption" style={{ color: 'var(--destructive)' }}>
          {error}
        </p>
      )}

      {stats && (
        <>
          {/* ------------------------------------------------------ top line */}
          <section className="stack" style={{ gap: 10 }}>
            <h2 className="t-eyebrow">Top line</h2>
            <div className="responsive-grid" style={grid}>
              <Stat
                value={stats.users.total}
                label="Total users"
                caption={stats.users.truncated ? 'First 1,000 accounts' : 'All accounts'}
              />
              <Stat
                value={stats.users.signupsLast30}
                label="Signups, 30 days"
                delta={delta(stats.users.signupsLast30, stats.users.signupsPrev30)}
                emphasised
              />
              <Stat value={stats.tours.total} label="Total tours" caption={`${stats.tours.upcoming} upcoming`} />
              <Stat
                value={stats.tours.createdLast30}
                label="Tours created, 30 days"
                delta={delta(stats.tours.createdLast30, stats.tours.createdPrev30)}
              />
            </div>
          </section>

          {/* --------------------------------------------------------- trend */}
          <section className="stack" style={{ gap: 10 }}>
            <h2 className="t-eyebrow">Last twelve weeks</h2>
            <div className="responsive-grid" style={{ ...grid, gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
              <WeeklyColumns
                heading="New accounts"
                unit="signups"
                data={stats.weeks.map((week) => ({ start: week.start, end: week.end, value: week.signups }))}
              />
              <WeeklyColumns
                heading="Tours created"
                unit="tours"
                data={stats.weeks.map((week) => ({ start: week.start, end: week.end, value: week.tours }))}
              />
              <WeeklyColumns
                heading="Shows added"
                unit="shows"
                data={stats.weeks.map((week) => ({ start: week.start, end: week.end, value: week.shows }))}
              />
            </div>
            <p className="t-caption" style={{ maxWidth: '70ch' }}>
              Each column is a full seven days ending today, not a calendar week — a week
              still in progress would render as a collapse in the last column.
            </p>
          </section>

          {/* -------------------------------------------------- does it stick */}
          <section className="stack" style={{ gap: 10 }}>
            <h2 className="t-eyebrow">Does it stick</h2>
            <div
              className="responsive-grid"
              style={{ ...grid, gridTemplateColumns: 'minmax(0, 1.1fr) minmax(0, 1fr)' }}
            >
              <div className="panel stack" style={{ gap: 18, padding: '18px 20px' }}>
                <Meter
                  label="Signed up and built a tour"
                  fraction={stats.activation.activationRate}
                  caption={`${stats.activation.withAtLeastOneTour} of ${stats.users.total} accounts`}
                />
                <Meter
                  label="Came back for a second tour"
                  fraction={stats.activation.repeatRate}
                  caption={`${stats.activation.repeatPlanners} of ${stats.activation.withAtLeastOneTour} planners`}
                />
                <Meter
                  label="Crew invites accepted"
                  fraction={stats.invites.acceptanceRate}
                  caption={`${stats.invites.accepted} of ${stats.invites.invited} invited`}
                />
              </div>

              <div className="stack" style={{ gap: 14 }}>
                <div className="responsive-grid" style={{ ...grid, gap: 12 }}>
                  <Stat value={stats.tours.active} label="On tour right now" emphasised />
                  <Stat value={stats.tours.showsNext30} label="Shows, next 30 days" />
                </div>
                <p className="t-caption" style={{ maxWidth: '46ch' }}>
                  Every tour drags four to ten people in. The share of them that accept is the
                  closest thing this product has to a growth loop — if it drops after a
                  release, something in the invite flow broke.
                </p>
              </div>
            </div>
          </section>

          {/* ------------------------------------------------------- activity */}
          <ActivityFeed events={stats.activity} />

          {/* --------------------------------------------------------- health */}
          <section className="stack" style={{ gap: 10 }}>
            <h2 className="t-eyebrow">Housekeeping</h2>
            <div className="responsive-grid" style={grid}>
              <Stat value={stats.users.unverified} label="Unverified email" />
              <Stat value={stats.users.disabled} label="Disabled accounts" />
              <Stat value={stats.tours.showsPerTour.toFixed(1)} label="Shows per tour" />
              <Stat value={stats.tours.crewPerTour.toFixed(1)} label="Crew per tour" />
            </div>
          </section>
        </>
      )}
    </AdminPage>
  )
}

/* ---------------------------------------------------------------- activity */

const KINDS: Array<{ id: ActivityKind | 'all'; label: string }> = [
  { id: 'all', label: 'Everything' },
  { id: 'signup', label: 'Signups' },
  { id: 'tour', label: 'Tours' },
  { id: 'show', label: 'Shows' },
  { id: 'crew', label: 'Crew' },
]

const KIND_ICON: Record<ActivityKind, IconName> = {
  signup: 'account',
  tour: 'tours',
  show: 'mic',
  crew: 'people',
}

/**
 * What has happened, newest first.
 *
 * Only events the documents actually date — anything written before `createdAt`
 * was added is genuinely undateable, and guessing a position for it would put
 * fiction at the top of the feed.
 */
function ActivityFeed({ events }: { events: AdminStats['activity'] }) {
  const [filter, setFilter] = useState<ActivityKind | 'all'>('all')

  const shown = useMemo(
    () => (filter === 'all' ? events : events.filter((event) => event.kind === filter)),
    [events, filter],
  )

  return (
    <section className="stack" style={{ gap: 10 }}>
      <div className="spread" style={{ alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h2 className="t-eyebrow">Recent activity</h2>
        <div className="row scroll-x" style={{ gap: 6 }}>
          {KINDS.map((kind) => (
            <button
              key={kind.id}
              type="button"
              className="chip"
              aria-pressed={filter === kind.id}
              onClick={() => setFilter(kind.id)}
              style={{
                flex: 'none',
                cursor: 'pointer',
                background: filter === kind.id ? 'var(--accent-wash)' : 'transparent',
                border: `var(--hairline-width) solid ${
                  filter === kind.id ? 'var(--accent)' : 'var(--hairline)'
                }`,
                color: filter === kind.id ? 'var(--accent-text)' : 'var(--text-secondary)',
              }}
            >
              {kind.label}
            </button>
          ))}
        </div>
      </div>

      {shown.length === 0 ? (
        <p className="panel panel-pad t-caption">Nothing recorded in that category yet.</p>
      ) : (
        <div className="panel rows">
          {shown.map((event) => (
            <div key={event.id} className="row" style={{ padding: '11px 16px', gap: 12 }}>
              <span
                aria-hidden
                style={{
                  display: 'grid',
                  placeItems: 'center',
                  width: 30,
                  height: 30,
                  flex: 'none',
                  borderRadius: 'var(--radius-control)',
                  background: 'var(--panel-raised)',
                  color: 'var(--accent-text)',
                }}
              >
                <Icon name={KIND_ICON[event.kind]} size={15} strokeWidth={1.7} />
              </span>
              <span className="grow stack" style={{ gap: 1, minWidth: 0 }}>
                <span className="truncate" style={{ fontWeight: 500 }}>
                  {event.title}
                </span>
                <span className="t-caption truncate">{event.detail}</span>
              </span>
              <time
                className="t-spec-caption faint"
                dateTime={new Date(event.at).toISOString()}
                title={new Date(event.at).toLocaleString()}
                style={{ whiteSpace: 'nowrap' }}
              >
                {ago(event.at)}
              </time>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

/* ------------------------------------------------------------------- stats */

const grid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
  gap: 14,
}

function Stat({
  value,
  label,
  caption,
  delta: change,
  emphasised,
}: {
  value: string | number
  label: string
  caption?: string
  delta?: { text: string; direction: 'up' | 'down' | 'flat' } | null
  emphasised?: boolean
}) {
  return (
    <div className="panel stack" style={{ gap: 4, padding: '18px 20px' }}>
      <span
        className="t-display-m"
        style={{ color: emphasised ? 'var(--accent-text)' : undefined }}
      >
        {typeof value === 'number' ? value.toLocaleString() : value}
      </span>
      <span className="t-eyebrow">{label}</span>
      {change && (
        // Direction is written out as well as coloured — an arrow on its own is
        // a colour-only signal, and up is not universally good.
        <span
          className="t-spec-caption"
          style={{
            color:
              change.direction === 'up'
                ? 'var(--positive)'
                : change.direction === 'down'
                  ? 'var(--destructive)'
                  : 'var(--text-tertiary)',
          }}
        >
          {change.text}
        </span>
      )}
      {caption && <span className="t-spec-caption faint">{caption}</span>}
    </div>
  )
}

function delta(
  current: number,
  previous: number,
): { text: string; direction: 'up' | 'down' | 'flat' } | null {
  if (previous === 0 && current === 0) return null
  const difference = current - previous
  if (difference === 0) return { text: 'Level on prior 30 days', direction: 'flat' }
  const sign = difference > 0 ? '+' : '−'
  const share = previous === 0 ? null : Math.round((difference / previous) * 100)
  return {
    text: `${sign}${Math.abs(difference)}${share === null ? '' : ` (${sign}${Math.abs(share)}%)`} vs prior 30 days`,
    direction: difference > 0 ? 'up' : 'down',
  }
}

const RELATIVE = new Intl.RelativeTimeFormat('en-GB', { numeric: 'auto' })

function ago(at: number): string {
  const seconds = (at - Date.now()) / 1000
  const steps: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['second', 60],
    ['minute', 60],
    ['hour', 24],
    ['day', 7],
    ['week', 4.35],
    ['month', 12],
  ]
  let value = seconds
  for (const [unit, size] of steps) {
    if (Math.abs(value) < size) return RELATIVE.format(Math.round(value), unit)
    value /= size
  }
  return RELATIVE.format(Math.round(value), 'year')
}
